import * as Sentry from "@sentry/react-native";
import { useEffect } from "react";
import { NativeEventEmitter, NativeModules, BackHandler } from "react-native";
import cleverTapAnalytics from "@services/analytics";
import HyperSdkReact from "hyper-sdk-react";
import { actions as configActions } from "@reducers/configReducer";
import { juspayStatus } from "@constants/juspayEnums";
import { useDispatch, useSelector } from "react-redux";

const _showLoader = () => {};
const _hideLoader = () => {};
const _initiateResult = ({ result, event, payload }) => {};
const _processResult = ({ result, event, payload }) => {};

const juspayBackHandler = () =>
  !HyperSdkReact.isNull() && HyperSdkReact.onBackPressed();

const selectors = {
  juspayEvents: (state) => state.configReducer?.juspayStatus,
  juspayPrefetchData: (state) => state.configReducer?.juspayPrefetchData,
  juspayInitiateData: (state) => state.configReducer?.juspayInitiateData,
};

const withJuspay = ({
  showLoader = _showLoader,
  hideLoader = _hideLoader,
  initiateResult = _initiateResult,
  processResult = _processResult,
}) => {
  const juspayEventStatus = useSelector(selectors.juspayEvents);
  const juspayPrefetchData = useSelector(selectors.juspayPrefetchData);
  const juspayInitiateData = useSelector(selectors.juspayInitiateData);
  const dispatch = useDispatch();

  const { preFetch, createHyperServices, initiate } = juspayEventStatus;

  useEffect(() => {
    const eventEmitter = new NativeEventEmitter(NativeModules.HyperSdkReact);

    const listener = eventEmitter.addListener("HyperEvent", (juspayPayload) => {
      const result = JSON.parse(juspayPayload);
      const event = result.event || "";
      const payload = result.payload || {};

      cleverTapAnalytics.trackEventCT(event);

      switch (event) {
        case "show_loader":
          return showLoader();
        case "hide_loader":
          return hideLoader();
        case "initiate_result":
          if (payload.status === juspayStatus.success) {
            dispatch(configActions.updateJuspayStatus({ initiate: true }));
          } else {
            Sentry.addBreadcrumb({
              category: "Juspay",
              message: "Juspay Init Error",
              data: payload,
            });
            Sentry.captureException(new Error("Juspay Init Error"));
          }
          return initiateResult({ result, event, payload });
        case "process_result":
          return processResult({ result, event, payload });
        default:
          Sentry.captureException(new Error(`Unknown HyperSDKEvent ${event}`));
      }
    });

    const backHandlerSub = BackHandler.addEventListener("hardwareBackPress", juspayBackHandler);

    return () => {
      backHandlerSub.remove();
      listener?.remove();
      // eventEmitter?.removeSubscription(listener);
    };
  }, [hideLoader, initiateResult, processResult, showLoader]);

  const processPayment = async (data) => {
    try {
      // Step-0: PreFetch

      if (!preFetch) {
        if (!juspayPrefetchData) {
          throw new Error("Juspay PreFetch data not found");
        }
        const isPreFetchValid =
          juspayPrefetchData && typeof juspayPrefetchData === "object";
        if (isPreFetchValid) {
          HyperSdkReact.preFetch(juspayPrefetchData);
          dispatch(configActions.updateJuspayStatus({ preFetch: true }));
        } else {
          throw new Error("Juspay PreFetch data not found");
        }
      }
      // Step-1: Create HyperServices Object

      if (!createHyperServices) {
        HyperSdkReact.createHyperServices();
        dispatch(
          configActions.updateJuspayStatus({ createHyperServices: true }),
        );
      }

      // Step-2: Initiate

      const hasInitialized = await HyperSdkReact.isInitialised();

      if (!hasInitialized || !initiate) {
        if (juspayInitiateData && typeof juspayInitiateData === "object") {
          HyperSdkReact.initiate(JSON.stringify(juspayInitiateData));
        } else {
          throw new Error("Juspay: Initiate data not found");
        }
      }

      // Step-3:  Process

      const processPayload = data.juspayProcessPayload;
      const isProcessPayloadValid =
        processPayload && typeof processPayload === "object";
      if (isProcessPayloadValid) {
        HyperSdkReact.process(JSON.stringify(processPayload));
      } else {
        throw new Error("Juspay: Invalid Process Payload");
      }
    } catch (e) {
      console.log(e);
      Sentry.captureException(e);
    }
  };
  return { processPayment };
};

export default withJuspay;
